/** @file Maps a placeholder of a slot to an attachment in a skin (replacing an earlier mapping). */
import { z } from 'zod';
import { prim } from '@pose2d/format/doc/schemaParts/primitives';
import { throwError } from '@pose2d/format/throwError';
import type { Doc, Id } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';
import { skinDraft } from './skinDraft';

/** MOUNT_INVALID at `slot` when a mounted attachment would land in a slot that is not on its socket bone (§5.9). */
function assertMountFits(doc: Doc, attId: Id, slotId: Id): void {
  const att = doc.attachments[attId];
  const socket = att?.mount ? doc.sockets[att.mount.socket] : undefined;
  if (!att || !socket || doc.slots[slotId]?.bone === socket.bone) return;
  throwError('MOUNT_INVALID', { attachment: att.name, reason: "the attachment's slot must sit on the socket bone" }, { path: 'slot', hint: `use a slot on bone ${socket.name}, or socket.equip` });
}

/** skin.setEntry: maps (slot, placeholder) → attachment, re-pointing an existing mapping; the same mapping is a no-op. */
export const command = defineCommand({
  name: 'skin.setEntry',
  description: 'In `skin` (the default skin when omitted) map `placeholder` of `slot` to `attachment`; an existing mapping of that placeholder on that slot is replaced.',
  input: z.object({ skin: z.string().min(1).exactOptional(), slot: z.string().min(1), placeholder: prim.name, attachment: z.string().min(1) }).strict(),
  apply(draft, input, ctx) {
    const skin = skinDraft(draft, ctx, input.skin);
    const slot = ctx.resolve('slot', input.slot);
    const attachment = ctx.resolve('attachment', input.attachment);
    assertMountFits(draft, attachment, slot);
    const same = skin.entries.find((e) => e.slot === slot && e.placeholder === input.placeholder);
    if (same?.attachment === attachment) return;
    if (same) same.attachment = attachment;
    else skin.entries.push({ slot, placeholder: input.placeholder, attachment });
    draft.meta.updatedAt = ctx.now;
  },
});
